import React, { useMemo } from "react";
import { Exercise } from "../../../models/Exercise";
import "../style.scss";
import { useTranslation } from "react-i18next";
import { Identifiable } from "../../../models/Identifiable";
import { formatExerciseClock, remainingPlaces, slotIsAvailable } from "./exerciseSlotHelpers";
import { useCalendarExerciseChoice } from "./useCalendarExerciseChoice";
import { DAY_PERIOD_LABEL_KEYS, groupSlotsByPeriod, LOW_CAPACITY_THRESHOLD } from "../CalendarModal/calendarFlowUtils";
import EAAvailabilityDots from "../../shared/AvailabilityDots/EAAvailabilityDots";
import { utcToZonedTime } from "date-fns-tz";
import { TbShoppingCart } from "react-icons/tb";

interface IProps {
  exercise: Exercise[];
  getTicketsByExercise: (id: Identifiable, exercises: Exercise) => void;
  handleWhichClicked: (index: number) => void;
  handleCloseModal?: (boolean: boolean) => void;
  getDetailsAboutClickedExercise: (exerciseId: number) => void;
  whichClicked: number;
  onTimeSelect?: () => void;
}

const CalendarExerciseChoice: React.FC<IProps> = ({
  getDetailsAboutClickedExercise,
  exercise,
  getTicketsByExercise,
  handleCloseModal,
  handleWhichClicked,
  whichClicked,
  onTimeSelect,
}) => {
  const { t } = useTranslation();
  const { showTimeOfEntance, showTicketsPool, getQntStateCount, handleTimeSelection } = useCalendarExerciseChoice({
    getDetailsAboutClickedExercise,
    getTicketsByExercise,
    handleCloseModal,
    handleWhichClicked,
    onTimeSelect,
  });

  // grupowanie wymaga przeliczenia stref czasowych dla każdego slotu – robimy to tylko przy zmianie listy godzin
  const groups = useMemo(
    () =>
      groupSlotsByPeriod(
        (exercise ?? []).map((item, index) => ({ item, index })),
        ({ item }) => utcToZonedTime(item.startTime, "Europe/Warsaw").getHours()
      ),
    [exercise]
  );

  if (!exercise || exercise.length === 0) {
    return <div className="no-available-exercises">{t("Brak dostępnych godzin")}</div>;
  }

  const renderSlot = ({ item, index }: { item: Exercise; index: number }) => {
    const available = slotIsAvailable(item);
    const selected = index === whichClicked;
    const remaining = remainingPlaces(item);
    const percentageFree = item.capacity > 0 ? (remaining / item.capacity) * 100 : 0;
    // Mały limit od początku (np. 3 miejsca), a nie duża pula, z której zostało niewiele.
    const smallCapacity = item.capacity > 0 && item.capacity <= LOW_CAPACITY_THRESHOLD;
    const showCountLabel = !!showTicketsPool && smallCapacity && remaining > 0;
    const inBasket = showTicketsPool ? getQntStateCount(String(item.objectId)) : 0;
    const className = [
      "flow-slot",
      selected ? "flow-slot--selected" : "",
      available ? "" : "flow-slot--sold",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={!available}
        key={item.objectId ?? index}
        className={className}
        onClick={() => handleTimeSelection(index, item.objectId, item.exerciseGroup, item)}
      >
        <span className="flow-slot__main">
          <span className="flow-slot__radio" aria-hidden={true} />
          <span className="flow-slot__time">
            <b>{formatExerciseClock(item.startTime)}</b>
            {showTimeOfEntance && <small>{t("slot_do", { time: formatExerciseClock(item.closeTime) })}</small>}
          </span>
        </span>
        {(showTicketsPool || inBasket > 0) && (
          <span className="flow-slot__side">
            {showCountLabel ? (
              <span className="flow-slot__cap">{t("slot_zostalo", { count: remaining })}</span>
            ) : (
              showTicketsPool && <EAAvailabilityDots percentageFree={percentageFree} />
            )}
            {inBasket > 0 && (
              <span
                className="flow-slot__basket"
                title={t("slot_wybrane_bilety", { count: inBasket })}
                aria-label={t("slot_wybrane_bilety", { count: inBasket })}
              >
                <TbShoppingCart aria-hidden={true} />
                {inBasket}
              </span>
            )}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flow-slots" role="radiogroup" aria-label={t("godzina_wejscia")}>
      {groups.map((group) => (
        <div className="flow-slots__group" key={group.period ?? "all"}>
          {group.period && <h3 className="flow-slots__period">{t(DAY_PERIOD_LABEL_KEYS[group.period])}</h3>}
          <div className="flow-slots__grid">{group.items.map(renderSlot)}</div>
        </div>
      ))}
    </div>
  );
};

export default CalendarExerciseChoice;
